/**
 * CustomScriptEditor - Configuration editor for Custom Scripts
 *
 * Allows users to add custom JavaScript to HEAD and/or BODY sections.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Box, TextField, Button, Alert, Typography } from '@mui/material';
import type { CustomScriptConfig, PlatformConfig } from '../../types/analytics.types';

interface CustomScriptEditorProps {
  config: PlatformConfig;
  onChange: (config: PlatformConfig, name?: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  showSaveButton?: boolean;
  onSave?: (config: PlatformConfig, name?: string) => void;
  name?: string;
}

export const CustomScriptEditor: React.FC<CustomScriptEditorProps> = ({
  config,
  onChange,
  disabled = false,
  readOnly = false,
  showSaveButton = false,
  onSave,
  name,
}) => {
  const customConfig = config as CustomScriptConfig;
  const [localConfig, setLocalConfig] = useState<CustomScriptConfig>(customConfig);
  const [localName, setLocalName] = useState(name || '');

  useEffect(() => {
    setLocalConfig(customConfig);
    setLocalName(name || '');
  }, [customConfig, name]);

  const handleChange = useCallback(
    (field: keyof CustomScriptConfig, value: unknown) => {
      const newConfig = { ...localConfig, [field]: value };
      setLocalConfig(newConfig);
      if (!showSaveButton) {
        onChange(newConfig, localName);
      }
    },
    [localConfig, localName, onChange, showSaveButton]
  );

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave(localConfig, localName);
    }
  }, [onSave, localConfig, localName]);

  return (
    <Box>
      {/* Script Name */}
      <TextField
        label="Script Name"
        value={localConfig.name || ''}
        onChange={(e) => handleChange('name', e.target.value)}
        disabled={disabled || readOnly}
        fullWidth
        sx={{ mb: 3 }}
        helperText="A descriptive name for this custom script"
      />

      {/* HEAD Script */}
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        HEAD Script
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Injected in &lt;head&gt; before page content loads. Use for loading external SDKs.
      </Typography>
      <TextField
        value={localConfig.headScript || ''}
        onChange={(e) => handleChange('headScript', e.target.value)}
        disabled={disabled || readOnly}
        fullWidth
        multiline
        rows={6}
        sx={{
          mb: 3,
          '& .MuiInputBase-input': {
            fontFamily: 'monospace',
            fontSize: '0.85rem',
          },
        }}
        placeholder={`// Raw JavaScript — do NOT wrap in <script> tags.
// Example: Meta Pixel base code, then fbq('init', 'YOUR_PIXEL_ID');`}
      />

      {/* BODY Script */}
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        BODY Script
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Injected at end of &lt;body&gt; after page loads. Use for event handlers and initialization.
      </Typography>
      <TextField
        value={localConfig.bodyScript || ''}
        onChange={(e) => handleChange('bodyScript', e.target.value)}
        disabled={disabled || readOnly}
        fullWidth
        multiline
        rows={10}
        sx={{
          mb: 2,
          '& .MuiInputBase-input': {
            fontFamily: 'monospace',
            fontSize: '0.85rem',
          },
        }}
        placeholder={`// Example: Custom event handler
window.customEventTracker = function(eventName, args) {
  // Google Analytics
  if (typeof gtag === 'function') {
    gtag('event', eventName, args);
  }

  // Custom tracking logic
  if (eventName === 'UserUpdatedValue') {
    const score = (args.userInputValue || []).length * 20;
    window.segmentlySDK.setCustomData('lead_score', score);
  }
};`}
      />

      {/* Info Alerts */}
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="caption">
          <strong>Events received:</strong> StartOnboarding, ScreenDidAppear, ScreenDisappeared,
          UserUpdatedValue, EmailCaptured, PaywallDecision, PurchaseFooterButtonPressed,
          OnboardingFinished, and more — full catalog: modules/analytics/events.md
        </Typography>
      </Alert>

      <Alert severity="warning" sx={{ mb: 2 }}>
        <Typography variant="caption">
          <strong>Custom Parameters:</strong> To use{' '}
          <code>window.segmentlySDK.setCustomData(key, value)</code>, you must first define the
          parameter in the <strong>Parameters</strong> section of your Web Placement.
        </Typography>
      </Alert>

      {showSaveButton && (
        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={disabled || !localConfig.name}
          >
            Save
          </Button>
        </Box>
      )}
    </Box>
  );
};
